import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarDays } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/src/renderer/components/ui/popover'
import { dateKey, parseDateKey } from '@/src/renderer/lib/date/calendar'
import { MiniCalendar } from '../WhenDropdown/MiniCalendar'
import './DatePickerField.scss'

export interface DatePickerFieldProps {
  /** 'YYYY-MM-DD' */
  value: string
  onValueChange: (value: string) => void
  /** Früheste wählbare Tag; davor ist der Kalender gesperrt. */
  min?: Date
  'aria-label': string
  /**
   * **Popover oder Aufklappen.** In den Aufgaben-Details öffnet der Kalender in einem
   * Radix-Popover (shadcn „Date Picker“). Im schwebenden Wähler stünde das als drittes
   * Portal in zwei anderen — dasselbe Gerangel um Fokus und Ebenen wie beim
   * Auswahlfeld (`PickerSelect`, `native`). Dort klappt der Kalender unter der Zeile auf.
   */
  portal: boolean
}

/**
 * **Ein Datumsfeld nach shadcn „Date Picker“** — Knopf mit Datum, darunter unser
 * `MiniCalendar` (derselbe wie bei Wann und Deadline). Ein Klick auf einen Tag wählt
 * und schließt. Das Ende einer **Wiederholung** (T13 #178) setzt man damit.
 *
 * @example
 * <DatePickerField value="2026-12-31" min={start} portal={inline}
 *   aria-label={t('recurrence.endDateLabel')} onValueChange={date => …} />
 */
export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  value, onValueChange, min, 'aria-label': ariaLabel, portal,
}) => {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const date = parseDateKey(value)
  const label = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(date)

  const calendar = (
    <MiniCalendar
      selectedDate={date}
      activeSelection
      minDate={min}
      onSelect={day => { onValueChange(dateKey(day)); setOpen(false) }}
    />
  )
  const trigger = (
    <button
      type="button"
      className="date-picker-field__trigger"
      aria-label={`${ariaLabel}: ${label}`}
      aria-expanded={open}
      onClick={portal ? undefined : () => setOpen(o => !o)}
    >
      <CalendarDays size={16} className="date-picker-field__icon" />
      <span>{label}</span>
    </button>
  )

  if (portal) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className="date-picker-field__panel" align="start">{calendar}</PopoverContent>
      </Popover>
    )
  }
  return (
    <>
      {trigger}
      {open && <div className="date-picker-field__inline">{calendar}</div>}
    </>
  )
}
